import React, { useMemo, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

// Helper for env in CRA or Vite
function getPaypalClientId() {
  const raw = process.env.REACT_APP_PAYPAL_CLIENT_ID || "";
  return raw.trim().replace(/^['"]|['"]$/g, ""); // strip accidental quotes
}

export default function PayNow() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [invoice, setInvoice] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const emailValid = /[^@\s]+@[^@\s]+\.[^@\s]+/.test(email);
  const amountValid = /^\d+(\.\d{1,2})?$/.test(amount) && parseFloat(amount) > 0;
  const nameValid = name.trim().length >= 2;
  const canSubmit = emailValid && amountValid && nameValid;

  const paypalClientId = getPaypalClientId();

  // Only load PayPal if we have a client id
  const ppOptions = useMemo(() => ({
    "client-id": paypalClientId,
    currency: "USD",                 // change if needed
    intent: "capture",
    components: "buttons",
  }), [paypalClientId]);

  async function notifyBackendAfterCapture({ orderId, payerEmail, payerName, grossAmount }) {
    // Your mail server in your private network
    const API_BASE = process.env.REACT_APP_API_BASE || ""; // keep this for local dev overrides
    const url = `${API_BASE}/api/mail/payment`;            // <- relative in prod
    const payload = {
      orderId,
      email: email || payerEmail,
      name: name || payerName,
      invoice,
      amount: grossAmount || amount,
      note,
      source: "PayNow.jsx",
    };

    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`Payment email notify failed: ${res.status} ${text}`);
    }
    return res.json().catch(() => ({}));
  }

  return (
    <section className="request container">
      <h2>Pay My Bill</h2>
      <p className="sub">Use a card or PayPal to complete a secure payment. You’ll receive an email confirmation.</p>
      <p className="sub">**A 4% surcharge will be applied to all credit card transactions.**</p>
      <form className="form" onSubmit={(e) => e.preventDefault()} noValidate>
        <div className="form-field">
          <label htmlFor="payerName">Your Name</label>
          <input
            id="payerName"
            name="payerName"
            type="text"
            placeholder="Your Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            aria-invalid={!nameValid && name.length > 0}
          />
          {!nameValid && name.length > 0 && (
            <div className="field-error">Please enter your full name.</div>
          )}
        </div>

        <div className="form-field">
          <label htmlFor="email">Receipt Email</label>
          <input
            id="email"
            name="email"
            type="email"
            inputMode="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            aria-invalid={!emailValid && email.length > 0}
          />
          {!emailValid && email.length > 0 && (
            <div className="field-error">Please enter a valid email address.</div>
          )}
        </div>

        <div className="form-field">
          <label htmlFor="invoice">Invoice / Reference # (optional)</label>
          <input
            id="invoice"
            name="invoice"
            type="text"
            placeholder="INV-12345 (optional)"
            value={invoice}
            onChange={(e) => setInvoice(e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="amount">Amount (USD)</label>
          <input
            id="amount"
            name="amount"
            type="number"
            inputMode="decimal"
            step="0.01"
            placeholder="100.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
            aria-invalid={!amountValid && amount.length > 0}
          />
          {!amountValid && amount.length > 0 && (
            <div className="field-error">Enter a valid amount (e.g., 100 or 100.50).</div>
          )}
        </div>

        <div className="form-field">
          <label htmlFor="note">Note (optional)</label>
          <textarea
            id="note"
            name="note"
            rows={4}
            placeholder="Add a note (optional)."
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        {/* We keep a disabled button purely for UX parity with your other forms */}
        <button className="btn-primary" type="button" disabled={!canSubmit || isSubmitting}>
          {isSubmitting ? "Processing..." : "Review with PayPal below"}
        </button>

        {error && <div className="alert alert-error" role="alert">{error}</div>}
        {successMsg && <div className="alert alert-success" role="status">{successMsg}</div>}
      </form>

      {/* PayPal Section */}
      {paypalClientId ? (
        <div style={{ marginTop: "1rem", maxWidth: 420, display: "flex", justifyContent: "center" }}>
          <PayPalScriptProvider options={ppOptions}>
            <PayPalButtons
              style={{ layout: "vertical", shape: "rect", label: "paypal" }}
              disabled={!canSubmit || isSubmitting}
              forceReRender={[amount]}
              createOrder={(_, actions) => {
                setError(null);
                setSuccessMsg(null);
                if (!canSubmit) return;
                return actions.order.create({
                  purchase_units: [
                    {
                      amount: { value: parseFloat(amount).toFixed(2) },
                      invoice_id: invoice || undefined,
                      description: note ? note.slice(0, 120) : "AZ Facility Management – Payment",
                    },
                  ],
                  payer: {
                    name: { given_name: name.split(" ")[0] || name, surname: name.split(" ").slice(1).join(" ") || "" },
                    email_address: email,
                  },
                  application_context: { shipping_preference: "NO_SHIPPING" },
                });
              }}
              onApprove={async (_, actions) => {
                try {
                  setIsSubmitting(true);
                  const details = await actions.order.capture();
                  const orderId = details?.id;
                  const payerEmail = details?.payer?.email_address;
                  const payerName = `${details?.payer?.name?.given_name || ""} ${details?.payer?.name?.surname || ""}`.trim();
                  const grossAmount =
                    details?.purchase_units?.[0]?.payments?.captures?.[0]?.amount?.value ||
                    details?.purchase_units?.[0]?.amount?.value;

                  // Notify your mail server AFTER capture
                  await notifyBackendAfterCapture({ orderId, payerEmail, payerName, grossAmount });

                  setSuccessMsg(`Payment received. Receipt sent to ${email || payerEmail}. Thank you!`);
                  setInvoice("");
                  setAmount("");
                  setNote("");
                } catch (err) {
                  console.error(err);
                  setError(err?.message || "Payment succeeded but notifying the server failed.");
                } finally {
                  setIsSubmitting(false);
                }
              }}
              onCancel={() => {
                setError("Payment was canceled.");
              }}
              onError={(err) => {
                console.error(err);
                setError("PayPal error. Please try again or use a different method.");
              }}
            />
          </PayPalScriptProvider>
        </div>
      ) : (
        <div className="alert alert-error" style={{ marginTop: "1rem" }}>
          Missing PayPal client ID. Set <code>REACT_APP_PAYPAL_CLIENT_ID</code> (CRA).
        </div>
      )}
    </section>
  );
}
